/**
 * Prototyper Service
 *
 * Handles prototype creation and management for conversations
 */

import type { AxiosError } from 'axios';
import apiClient from '../client';
import { ApiUrls } from '../ApiUrls';
import type {
  Prototype,
  PrototypeCreationResponse,
  UpdatePrototyperDemoLinkParams,
  PrototyperBySlugResponse,
  PrototypeSnapshotPrepareResponse,
  PrototypeSnapshotFinalizeResponse,
  PrototypeSnapshotLatestResponse,
  PrototypePublishResponse,
  ConversationPrototypeWithVersions,
  PrototypeVersion,
  PrototypeTurnVersion,
  PrototypeVersionBuildResult,
  PrototypeVersionFiles,
  PrototypeVersionDetail,
  PrototypeVersionFailResult,
  PrototypeVersionFixAttemptResult,
} from '../../types/prototyper.types';

const emptyOn404 = <T>(err: unknown, fallback: T): T => {
  const status = (err as AxiosError | undefined)?.response?.status;
  if (status === 404) return fallback;
  throw err;
};

const toIso = (value: unknown): string | undefined => {
  if (value == null) return undefined;
  if (value instanceof Date) return value.toISOString();
  return String(value);
};

const normalizePrototype = (raw: any): Prototype => ({
  id: String(raw.id),
  title: raw.title ?? null,
  summary: raw.summary ?? null,
  conversationId: raw.conversationId ?? raw.conversation_id,
  projectId: raw.projectId ?? raw.project_id,
  scopeId: raw.scopeId ?? raw.scope_id ?? null,
  lastBuiltInteractionId:
    raw.lastBuiltInteractionId ?? raw.last_built_interaction_id ?? null,
  createdAt: toIso(raw.createdAt ?? raw.created_at),
  updatedAt:
    raw.updatedAt == null && raw.updated_at == null
      ? null
      : toIso(raw.updatedAt ?? raw.updated_at) ?? null,
});

const unwrapPrototypeList = (body: any): Prototype[] => {
  const rows = Array.isArray(body)
    ? body
    : Array.isArray(body?.data)
      ? body.data
      : [];
  return rows.map(normalizePrototype);
};

const unwrapPrototype = (body: any): Prototype => {
  const raw =
    body && typeof body === 'object' && 'data' in body && !('id' in body)
      ? (body as { data: unknown }).data
      : body;
  return normalizePrototype(raw);
};

class PrototyperService {
  /**
   * Update a prototyper demo link (slug and/or header)
   */
  async updatePrototyperDemoLink(
    params: UpdatePrototyperDemoLinkParams
  ): Promise<PrototypeCreationResponse> {
    try {
      const { id, slug } = params;
      const url = ApiUrls.updatePrototyperDemoLink(id);

      const payload: any = {};
      if (slug !== undefined) payload.slug = slug;

      const response = await apiClient.patch<PrototypeCreationResponse>(url, payload);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Unpublish one version of a prototype. The other published versions keep their links; the
   * response is the link after it (slug null when that was the last published version).
   */
  async unpublishPrototypeVersion(
    id: number,
    versionId: string
  ): Promise<PrototypeCreationResponse> {
    try {
      const url = ApiUrls.unpublishPrototypeVersion(id, versionId);
      const response = await apiClient.delete<PrototypeCreationResponse>(url);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get prototyper demo link by slug
   */
  async getPrototyperBySlug(slug: string): Promise<PrototyperBySlugResponse> {
    try {
      const url = ApiUrls.getPrototyperBySlug(slug);
      const response = await apiClient.get<PrototyperBySlugResponse>(url);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get prototyper demo link by ID
   */
  async getPrototyperById(id: string | number): Promise<PrototypeCreationResponse> {
    try {
      const url = ApiUrls.getPrototyperById(id);
      const response = await apiClient.get<PrototypeCreationResponse>(url);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /** GET /prototypes?scope_id=... */
  async listScopePrototypes(scopeId: string): Promise<Prototype[]> {
    try {
      const response = await apiClient.get(ApiUrls.listScopePrototypes(scopeId));
      return unwrapPrototypeList(response.data);
    } catch (err) {
      return emptyOn404(err, [] as Prototype[]);
    }
  }

  /** GET /prototypes?project_id=... */
  async listStartupPrototypes(projectId: string): Promise<Prototype[]> {
    try {
      const response = await apiClient.get(ApiUrls.listStartupPrototypes(projectId));
      return unwrapPrototypeList(response.data);
    } catch (err) {
      return emptyOn404(err, [] as Prototype[]);
    }
  }

  /** GET /prototypes/:id */
  async getPrototypeById(id: string): Promise<Prototype> {
    const response = await apiClient.get(ApiUrls.getPrototypeById(id));
    return unwrapPrototype(response.data);
  }

  /**
   * Presigned PUT upload for conversation prototype snapshot (zip).
   */
  async preparePrototypeSnapshot(
    projectId: string,
    conversationId: string,
    llmInteractionId?: string
  ): Promise<PrototypeSnapshotPrepareResponse> {
    try {
      const url = ApiUrls.prototypeSnapshotPrepare(projectId, conversationId);
      const response = await apiClient.post<PrototypeSnapshotPrepareResponse>(url, {
        ...(llmInteractionId ? { llmInteractionId } : {}),
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async finalizePrototypeSnapshot(
    projectId: string,
    conversationId: string,
    body: {
      snapshotId: string;
      etag?: string;
      sizeBytes?: number;
      llmInteractionId?: string;
      buildVerified?: boolean;
      /** The turn's snapshot revision this save started from (P6.4e). */
      baseRevision?: number;
      /** Save even though another tab saved this turn in between. */
      overwrite?: boolean;
    }
  ): Promise<PrototypeSnapshotFinalizeResponse> {
    try {
      const url = ApiUrls.prototypeSnapshotFinalize(projectId, conversationId);
      const response = await apiClient.post<PrototypeSnapshotFinalizeResponse>(url, body);
      return response.data;
    } catch (error: any) {
      // Another tab saved this turn after this tab loaded it (P6.4e).
      if (error?.response?.status === 409 && error?.response?.data?.code === 'SNAPSHOT_STALE') {
        const stale = new Error('This prototype was saved in another tab after you opened it');
        stale.name = 'PrototypeSnapshotStaleError';
        throw stale;
      }
      throw this.handleError(error);
    }
  }

  /**
   * Build zip on the client, presign PUT to S3 (no axios on upload URL), then finalize.
   */
  async uploadPrototypeSnapshotZip(
    projectId: string,
    conversationId: string,
    zipBlob: Blob,
    llmInteractionId?: string,
    buildVerified?: boolean,
    options?: { baseRevision?: number; overwrite?: boolean }
  ): Promise<PrototypeSnapshotFinalizeResponse | void> {
    if (zipBlob.size === 0) return;
    const prepare = await this.preparePrototypeSnapshot(
      projectId,
      conversationId,
      llmInteractionId
    );
    if (!prepare?.snapshotId || !prepare.uploadUrl || typeof prepare.maxBytes !== 'number') {
      throw new Error('Invalid snapshot prepare response from server');
    }
    if (zipBlob.size > prepare.maxBytes) {
      throw new Error(
        `Prototype snapshot too large (${zipBlob.size} bytes; max ${prepare.maxBytes})`
      );
    }
    const putRes = await fetch(prepare.uploadUrl, {
      method: 'PUT',
      body: zipBlob,
      headers: { 'Content-Type': 'application/zip' },
    });
    if (!putRes.ok) {
      throw new Error(`Snapshot upload failed (${putRes.status})`);
    }
    const rawEtag = putRes.headers.get('etag');
    const etag =
      rawEtag != null ? rawEtag.replace(/^W\//, '').replace(/"/g, '') || undefined : undefined;
    return this.finalizePrototypeSnapshot(projectId, conversationId, {
      snapshotId: prepare.snapshotId,
      etag,
      sizeBytes: zipBlob.size,
      ...(llmInteractionId ? { llmInteractionId } : {}),
      ...(buildVerified !== undefined ? { buildVerified } : {}),
      ...(options?.baseRevision !== undefined ? { baseRevision: options.baseRevision } : {}),
      ...(options?.overwrite ? { overwrite: true } : {}),
    });
  }

  async getPrototypeSnapshotByInteraction(
    projectId: string,
    conversationId: string,
    llmInteractionId: string
  ): Promise<PrototypeSnapshotLatestResponse> {
    try {
      const url = ApiUrls.prototypeSnapshotByInteraction(
        projectId,
        conversationId,
        llmInteractionId
      );
      const response = await apiClient.get<PrototypeSnapshotLatestResponse>(url);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Stop button: abort the Chef generation now. Closing the build-stream alone only ends the
   * current step, so the turn's work is saved (PROTOTYPER_STABILITY_TECH_PLAN.md P6.2).
   */
  // ── Prototype versions (plan P6.4) ─────────────────────────────────────────────

  /** Each prototype of the conversation with its current and latest version. */
  async listConversationPrototypes(
    projectId: string,
    conversationId: string
  ): Promise<ConversationPrototypeWithVersions[]> {
    const response = await apiClient.get<ConversationPrototypeWithVersions[]>(
      ApiUrls.conversationPrototypes(projectId, conversationId)
    );
    return response.data;
  }

  /** One version per turn of the conversation, newest first, with its prototype's Chef project. */
  async listConversationTurnVersions(
    projectId: string,
    conversationId: string
  ): Promise<PrototypeTurnVersion[]> {
    const response = await apiClient.get<PrototypeTurnVersion[]>(
      ApiUrls.prototypeVersions(projectId, conversationId)
    );
    return response.data;
  }

  /**
   * Every version of one prototype, oldest first. For a startup's shared prototype this spans
   * every scope, and each version says where and by whom it was made (User Story 569).
   */
  async listPrototypeVersions(
    projectId: string,
    conversationId: string,
    prototypeId: string
  ): Promise<PrototypeVersion[]> {
    const response = await apiClient.get<PrototypeVersion[]>(
      ApiUrls.prototypeVersionsOfPrototype(projectId, conversationId, prototypeId)
    );
    return response.data;
  }

  /** Create or return the draft version for a turn. */
  async createPrototypeVersion(
    projectId: string,
    conversationId: string,
    body: { llmInteractionId: string; chefProjectId?: string; prototypeDisplayName?: string }
  ): Promise<PrototypeVersion> {
    const response = await apiClient.post<PrototypeVersion>(
      ApiUrls.prototypeVersions(projectId, conversationId),
      body
    );
    return response.data;
  }

  async getPrototypeVersion(
    projectId: string,
    conversationId: string,
    versionId: string
  ): Promise<PrototypeVersionDetail> {
    const response = await apiClient.get<PrototypeVersionDetail>(
      ApiUrls.prototypeVersion(projectId, conversationId, versionId)
    );
    return response.data;
  }

  async leasePrototypeVersion(
    projectId: string,
    conversationId: string,
    versionId: string,
    owner: string
  ): Promise<{ acquired: boolean; leaseOwner: string | null; leaseExpiresAt: string | null }> {
    const response = await apiClient.post(
      `${ApiUrls.prototypeVersion(projectId, conversationId, versionId)}/lease`,
      { owner }
    );
    return response.data;
  }

  async verifyPrototypeVersion(
    projectId: string,
    conversationId: string,
    versionId: string,
    body: { contentHash?: string; snapshotId?: string; chefProjectId?: string }
  ): Promise<PrototypeVersion> {
    const response = await apiClient.post<PrototypeVersion>(
      `${ApiUrls.prototypeVersion(projectId, conversationId, versionId)}/verify`,
      body
    );
    return response.data;
  }

  async failPrototypeVersion(
    projectId: string,
    conversationId: string,
    versionId: string,
    body: {
      kind: 'code' | 'environment';
      errorSignature?: string;
      buildLog?: string;
      reason?: 'timeout';
    }
  ): Promise<PrototypeVersionFailResult> {
    const response = await apiClient.post<PrototypeVersionFailResult>(
      `${ApiUrls.prototypeVersion(projectId, conversationId, versionId)}/fail`,
      body
    );
    return response.data;
  }

  /** The draft's files as the builder saved them; 409 when they are not this version's. */
  /**
   * Write the founder's saved edits of a version into the builder's copy of the prototype, so
   * the next edit turn starts from them even after a page reload. 409 unless the version is its
   * prototype's newest, or while a build runs. Returns the saved paths.
   */
  async savePrototypeVersionFiles(
    projectId: string,
    conversationId: string,
    versionId: string,
    files: Record<string, string>
  ): Promise<{ versionId: string; saved: string[] }> {
    const response = await apiClient.put<{ versionId: string; saved: string[] }>(
      `${ApiUrls.prototypeVersion(projectId, conversationId, versionId)}/files`,
      { files }
    );
    return response.data;
  }

  async getPrototypeVersionFiles(
    projectId: string,
    conversationId: string,
    versionId: string
  ): Promise<PrototypeVersionFiles> {
    const response = await apiClient.get<PrototypeVersionFiles>(
      `${ApiUrls.prototypeVersion(projectId, conversationId, versionId)}/files`
    );
    return response.data;
  }

  /**
   * Build this version's saved files on the builder and settle the version from the verdict
   * (light-preview plan F1) — for a turn whose browser went away mid-build. No model turn runs
   * and no credits are spent. 409 when the version is not the newest, or the builder's saved
   * files belong to another turn.
   */
  async buildPrototypeVersion(
    projectId: string,
    conversationId: string,
    versionId: string
  ): Promise<PrototypeVersionBuildResult> {
    const response = await apiClient.post<PrototypeVersionBuildResult>(
      `${ApiUrls.prototypeVersion(projectId, conversationId, versionId)}/build`
    );
    return response.data;
  }

  /** Count one automatic fix attempt; `allowed: false` means the card must ask the founder. */
  async claimPrototypeFixAttempt(
    projectId: string,
    conversationId: string,
    versionId: string
  ): Promise<PrototypeVersionFixAttemptResult> {
    const response = await apiClient.post<PrototypeVersionFixAttemptResult>(
      `${ApiUrls.prototypeVersion(projectId, conversationId, versionId)}/fix-attempts`
    );
    return response.data;
  }

  /** "Try another fix". */
  async grantPrototypeFixAttempts(
    projectId: string,
    conversationId: string,
    versionId: string
  ): Promise<PrototypeVersion> {
    const response = await apiClient.post<PrototypeVersion>(
      `${ApiUrls.prototypeVersion(projectId, conversationId, versionId)}/grant-fixes`
    );
    return response.data;
  }

  /**
   * "Undo this change", and Remove on a build that did not finish. `owner` is this tab's id:
   * the backend refuses a version another tab is building, and must not take this tab's own
   * leftover build lease for one.
   */
  async discardPrototypeVersion(
    projectId: string,
    conversationId: string,
    versionId: string,
    owner?: string
  ): Promise<{ status: string; currentVersionId: string | null }> {
    const response = await apiClient.post(
      `${ApiUrls.prototypeVersion(projectId, conversationId, versionId)}/discard`,
      owner ? { owner } : {}
    );
    return response.data;
  }

  /**
   * "Build again" on a build that crashed or failed (Task 722): the version goes back to a fresh
   * draft. `base` is the finished version the build will start from (null: from scratch). The
   * caller then starts the turn's build stream with `rebuild: true`. `owner` is the tab asking,
   * as for Remove.
   */
  async rebuildPrototypeVersion(
    projectId: string,
    conversationId: string,
    versionId: string,
    owner?: string
  ): Promise<{
    status: string;
    base: { versionId: string; llmInteractionId: string; versionNumber: number | null } | null;
  }> {
    const response = await apiClient.post(
      `${ApiUrls.prototypeVersion(projectId, conversationId, versionId)}/rebuild`,
      owner ? { owner } : {}
    );
    return response.data;
  }

  async abortBuildStream(
    projectId: string,
    conversationId: string,
    chefProjectId: string
  ): Promise<{ aborted: boolean }> {
    const response = await apiClient.post<{ aborted: boolean }>(
      ApiUrls.projectConversationBuildStreamAbort(projectId, conversationId),
      { projectId: chefProjectId }
    );
    return response.data;
  }

  async createPrototyperBuildSession(
    llmInteractionId: string,
    body: { prototypeDisplayName?: string; buildTimeline?: Record<string, unknown> }
  ): Promise<{ id: string }> {
    const response = await apiClient.post(
      ApiUrls.prototypingBuildSessionLlm(llmInteractionId),
      body
    );
    return response.data;
  }

  async patchPrototyperBuildSession(
    llmInteractionId: string,
    body: Record<string, unknown>
  ): Promise<void> {
    await apiClient.patch(ApiUrls.prototypingBuildSessionLlm(llmInteractionId), body);
  }

  async createTmpPrototyperBuildSession(
    tmpLlmInteractionId: string,
    body: { prototypeDisplayName?: string; buildTimeline?: Record<string, unknown> }
  ): Promise<{ id: string }> {
    const response = await apiClient.post(
      ApiUrls.prototypingBuildSessionTmp(tmpLlmInteractionId),
      body
    );
    return response.data;
  }

  async patchTmpPrototyperBuildSession(
    tmpLlmInteractionId: string,
    body: Record<string, unknown>
  ): Promise<void> {
    await apiClient.patch(ApiUrls.prototypingBuildSessionTmp(tmpLlmInteractionId), body);
  }

  async getPrototypeSnapshotIndex(
    projectId: string,
    conversationId: string
  ): Promise<
    Array<{
      llmInteractionId: string;
      snapshotId: string;
      revision: number;
      buildVerified?: boolean;
      createdAt: string;
      prototyperDemoLinkId?: string;
    }>
  > {
    try {
      const url = ApiUrls.prototypeSnapshotIndex(projectId, conversationId);
      const response = await apiClient.get<
        Array<{
          llmInteractionId: string;
          snapshotId: string;
          revision: number;
          buildVerified?: boolean;
          createdAt: string;
          prototyperDemoLinkId?: string;
        }>
      >(url);
      return response.data ?? [];
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Trusted publish: builds snapshot on server and deploys static assets to demo CDN/S3.
   */
  async publishPrototype(
    projectId: string,
    conversationId: string,
    // `llmInteractionId`: the turn whose version to publish. The backend writes that turn's
    // prototype's one link; without it, the conversation's newest link (old clients).
    body: { slug: string; snapshotId?: string; llmInteractionId?: string }
  ): Promise<PrototypePublishResponse> {
    try {
      const url = ApiUrls.prototypePublish(projectId, conversationId);
      const response = await apiClient.post<PrototypePublishResponse>(url, body, {
        timeout: 600_000,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   */
  private handleError(error: any): Error {
    if (error.response) {
      const data = error.response.data;
      let message = data?.message;
      if (typeof message === 'object' && message !== null) {
        const detail = (message as any).detail || (message as any).message;
        message = typeof detail === 'string' ? detail : JSON.stringify(message).slice(0, 2000);
      }
      if (!message || typeof message !== 'string') {
        message = 'Request failed';
      }
      return new Error(message);
    }

    // Network error
    return new Error('Network error. Please check your connection.');
  }
}

export const prototyperService = new PrototyperService();
export default prototyperService;
